import {
  EDITOR_CREATE_GROUP_WIDGET, EDITOR_UNGROUP_WIDGET, EDITOR_DELETE_WIDGET
} from 'constants/actionTypes';

import { put, select, takeEvery, all, call, delay } from '@redux-saga/core/effects';
import { createAction } from 'redux-actions';
import { Frame } from "scenejs";
import shortid from 'shortid';
import _ from 'lodash';

import { getBoundingRectFromLayout, getWidgetBoundingRectForBreakpoint } from 'components/Editor/helper/layoutManager';
import { 
  actionGroup, createWidget, deleteWidgets, updateWidgetLayout, attachWidgetToContainer,
  bulkUpdateWidgetLayouts, processGroupWidgetLayout, applyWidgetPropertyChanges
 } from "./editor";

import { GROUP } from 'constants/widgetTypes';

export const createGroupWidget = createAction(EDITOR_CREATE_GROUP_WIDGET, (editorSession, selectedWidgets) => {
  return {
    editorSession: editorSession,
    selectedWidgets: selectedWidgets
  }
});

function* createGroupWidgetSaga(action) {
  const { editorSession, selectedWidgets } = action.payload;

  const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const breakpoint = yield select(state => _.get(state, `${editorSession}.present.breakpoint`) || []);

  const parent = _.get(selectedWidgets, '[0].parent', null);
  var uid = `widget_${shortid.generate()}`;

  var children = selectedWidgets.filter(widget => widget.parent === parent);
  children = _.orderBy(children, 'order', 'asc');

  var groupWidgets = children.filter(widget => widget.type === GROUP);

  children = _.reduce(children, (newArray, item) => {
    if(item.type === GROUP) {
      
      let groupChildren = widgets.filter(w => w.parent === item.uid);
      groupChildren = _.orderBy(groupChildren, 'order', 'asc');

      return [...newArray, ...groupChildren];
    } else {
      return [...newArray, item];
    }
  }, [])


  let order = 1;
  children = children.map(child => {

    const _findParent = (parent) => {
      return widgets.find(w => w.uid === parent);
    }

    let widgetParent = _findParent(child.parent);

    if (widgetParent.type === GROUP) {

      const _getChildLayout = (parent, child, breakpoint) => {
        const parentLayout = _.get(parent, `properties.${breakpoint}.layout`);
        const parentRect = getBoundingRectFromLayout(parentLayout);

        const childLayout = _.get(child, `properties.${breakpoint}.layout`);
        const childRect = getBoundingRectFromLayout(childLayout);

        const newX = parentRect.x + childRect.x;
        const newY = parentRect.y + childRect.y;

        const frame = new Frame(childLayout);
        frame.set('transform', 'translate', `${newX}px, ${newY}px`);

        return frame.get();
      }

      const desktopLayout = _getChildLayout(widgetParent, child, 'desktop');
      const mobileLayout = _getChildLayout(widgetParent, child, 'mobile');

      return {
        ...child,
        order: order,
        properties: {
          ...child.properties,
          desktop: {
            ...child.properties.desktop,
            layout: desktopLayout
          },
          mobile: {
            ...child.properties.mobile,
            layout: mobileLayout
          }
        }
      }
    }

    _.add(order, 1);

    return child;
  });

  const desktopRequest = calculateRequestForGroupWidget(editorSession, children, 'desktop');
  children = desktopRequest.widgets;

  const mobileRequest = calculateRequestForGroupWidget(editorSession, children, 'mobile');
  children = mobileRequest.widgets;

  actionGroup.start();

  if (groupWidgets.length === 0) {
    var request = {
      uid: uid,
      name: "Group",
      parent: parent,
      type: GROUP,
      properties: {
        desktop: {
          visible: true,
          layout: {
            transform: {
              translate: `${desktopRequest.x}px, ${desktopRequest.y}px`,
              rotate: "0deg",
            },
            width: `${desktopRequest.width}px`,
            height: `${desktopRequest.height}px`,
            minWidth: `${desktopRequest.width}px`,
            minHeight: `${desktopRequest.height}px`
          }
        },
        mobile: {
          visible: true,
          layout: {
            transform: {
              translate: `${mobileRequest.x}px, ${mobileRequest.y}px`,
              rotate: "0deg",
            },
            width: `${mobileRequest.width}px`,
            height: `${mobileRequest.height}px`,
            minWidth: `${mobileRequest.width}px`,
            minHeight: `${mobileRequest.height}px`
          }
        },
      }
    }

    yield put(createWidget(editorSession, [request], parent));
  }

  if (groupWidgets.length > 0) {
    var groupWidget = groupWidgets[0];
    uid = groupWidget.uid;

    var desktopLayout = _.get(groupWidget, 'properties.desktop.layout', {});
    desktopLayout = {
      ...desktopLayout,
      transform: {
        ...desktopLayout.transform,
        translate: `${desktopRequest.x}px, ${desktopRequest.y}px`
      },
      width: `${desktopRequest.width}px`,
      height: `${desktopRequest.height}px`,
      minWidth: `${desktopRequest.width}px`,
      minHeight: `${desktopRequest.height}px`
    }

    var mobileLayout = _.get(groupWidget, 'properties.mobile.layout', {});
    mobileLayout = {
      ...mobileLayout,
      transform: {
        ...mobileLayout.transform,
        translate: `${mobileRequest.x}px, ${mobileRequest.y}px`
      },
      width: `${mobileRequest.width}px`,
      height: `${mobileRequest.height}px`,
      minWidth: `${mobileRequest.width}px`,
      minHeight: `${mobileRequest.height}px`
    }

    yield put(updateWidgetLayout(editorSession, uid, 'desktop', desktopLayout));
    yield put(updateWidgetLayout(editorSession, uid, 'mobile', mobileLayout));
  }

  var updatedWidgets = [...children].map(item => {
    return put(attachWidgetToContainer(editorSession, uid, item.uid));
  });
  yield all(updatedWidgets);

  var updateDesktopLayouts = [...children].map(item => {
    return put(updateWidgetLayout(editorSession, item.uid, 'desktop', _.get(item, 'properties.desktop.layout')));
  });
  yield all(updateDesktopLayouts);

  var updateMobileLayouts = [...children].map(item => {
    return put(updateWidgetLayout(editorSession, item.uid, 'mobile', _.get(item, 'properties.mobile.layout')));
  });
  yield all(updateMobileLayouts);

  var deletedWidgets = groupWidgets.filter(w => w.uid !== uid).map(w => w.uid);

  yield put(deleteWidgets(editorSession, deletedWidgets));

  if(breakpoint === 'mobile') {
    var mobileLayout = _.get(groupWidget, 'properties.mobile.layout', {});

      const newMobileLayout = {
      ...mobileLayout,
      transform: {
        ...mobileLayout.transform,
        translate: `${mobileRequest.x}px, ${mobileRequest.y}px`
      },
      width: `${mobileRequest.width}px`,
      height: `${mobileRequest.height}px`,
      minWidth: `${mobileRequest.width}px`,
      minHeight: `${mobileRequest.height}px`
    }

    yield put(updateWidgetLayout(editorSession, uid, 'mobile', newMobileLayout));
  }

  actionGroup.end();
}

export const unGroupWidget = createAction(EDITOR_UNGROUP_WIDGET, (editorSession, widget) => {
  return {
    editorSession: editorSession,
    widget: widget
  }
})

function* unGroupWidgetSaga(action) {
  const { editorSession, widget } = action.payload;

  const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);

  var children = [...widgets].filter(item => item.parent === widget);
  children = _.orderBy(children, 'order', 'asc');

  var groupWidget = widgets.find(item => item.uid === widget);

  var processedWidgets = calculateRequestForUngroupWidget(groupWidget, children, 'desktop');
  processedWidgets = calculateRequestForUngroupWidget(groupWidget, processedWidgets, 'mobile');

  actionGroup.start();

  var updatedWidgets = [...processedWidgets].map(item => {
    return put(attachWidgetToContainer(editorSession, groupWidget.parent, item.uid));
  });
  yield all(updatedWidgets);

  var updateDesktopLayouts = [...processedWidgets].map(item => {
    return put(updateWidgetLayout(editorSession, item.uid, 'desktop', _.get(item, 'properties.desktop.layout')));
  });
  yield all(updateDesktopLayouts);

  var updateMobileLayouts = [...processedWidgets].map(item => {
    return put(updateWidgetLayout(editorSession, item.uid, 'mobile', _.get(item, 'properties.mobile.layout')));
  });
  yield all(updateMobileLayouts);

  yield put(deleteWidgets(editorSession, [groupWidget.uid]));

  actionGroup.end();
}

export const deleteWidgetInGroup = createAction(EDITOR_DELETE_WIDGET, (editorSession, widgets) => {
  return {
    editorSession: editorSession,
    widgets: widgets
  }
});

function* deleteWidgetInGroupSaga(action) {
  const { editorSession, widgets } = action.payload;

  const currentWidgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const breakpoint = yield select(state => _.get(state, `${editorSession}.present.breakpoint`) || 'desktop');

  const deleteWidgets = yield select(state => {
    const pastStates = _.get(state, `${editorSession}.past`) || [];

    const lastState = _.size(pastStates) - 1;
    if (lastState < 0) {
      return [];
    }

    const availbleWidgets = _.get(state, `${editorSession}.past[${lastState}].widgets`) || [];
    const result = availbleWidgets.filter(item => {
      return _.includes(widgets, item.uid);
    });

    return result;
  });

  var groupWidgets = [];
  deleteWidgets.forEach(item => {
    const _findParent = (parent) => {
      return currentWidgets.find(w => w.uid === parent);
    }

    let parent = _findParent(item.parent);

    if(parent && parent.type === GROUP) {
      groupWidgets = [...groupWidgets, parent.uid];
    }
  })

  var unGroupWidgets = [];
  var updatedWidgets = [];
  if(groupWidgets.length > 0) {
    groupWidgets.forEach(id => {
      const _findChildren = (parent) => {
        return currentWidgets.filter(w => w.parent === parent);
      }

      let children = _findChildren(id);

      if(children.length <= 1) {
        unGroupWidgets = [...unGroupWidgets, id];
      } else {
        updatedWidgets = [...updatedWidgets, id];
      }
    })
  }

  actionGroup.start();

  if(unGroupWidgets.length > 0) {
    yield all(unGroupWidgets.map(id => {
      return put(unGroupWidget(editorSession, id));
    }));
  }

  if(updatedWidgets.length > 0) {
    var changes = {};
    updatedWidgets.forEach(id => {
      var groupLayoutRequests = processGroupWidgetLayout(id, breakpoint, currentWidgets, [], editorSession);
      
      groupLayoutRequests.forEach(item => {
        changes[item.id] = {
          id: item.id,
          path: `properties.${breakpoint}.layout`,
          value: item.value
        };
      })
    })

    yield put(applyWidgetPropertyChanges(editorSession, changes, breakpoint));
  }

  actionGroup.end();
}

const calculateRequestForGroupWidget = (editorSession, widgets, breakpoint) => {
  var left = Number.MAX_SAFE_INTEGER;
  var top = Number.MAX_SAFE_INTEGER;
  var bottom = 0;
  var right = 0;

  const getWidgetRect = (widget) => {
    const layout = _.get(widget, `properties.${breakpoint}.layout`);
    var wRect = getBoundingRectFromLayout(layout);

    // eslint-disable-next-line no-self-compare
    if (wRect.width !== wRect.width || wRect.height !== wRect.height) {
      var boundRect = getWidgetBoundingRectForBreakpoint(editorSession, widget, breakpoint);
      wRect = {
        ...wRect,
        width: boundRect ? boundRect.width : 0,
        height: boundRect ? boundRect.height : 0
      }
    }

    return wRect;
  }

  widgets.forEach(widget => {
    const wRect = getWidgetRect(widget);
    if (wRect) {
      left = Math.min(wRect.x, left);
      top = Math.min(wRect.y, top);

      right = Math.max(right, wRect.x + wRect.width);
      bottom = Math.max(bottom, wRect.y + wRect.height);
    }
  });

  const width = Math.ceil(right - left);
  const height = Math.ceil(bottom - top);

  const updatedWidgets = widgets.map(item => {
  const layout = _.get(item, `properties.${breakpoint}.layout`);
  const rect = getWidgetRect(item);

  const newX = rect.x - left;
  const newY = rect.y - top;

  const frame = new Frame(layout);
  frame.set('transform', 'translate', `${newX}px, ${newY}px`);

  return {
    ...item,
    properties: {
      ...item.properties,
      [breakpoint]: {
        ...item.properties[breakpoint],
        layout: frame.get()
      }
    }
  }
  });

  return {
    x: left,
    y: top,
    width: width,
    height: height,
    widgets: updatedWidgets
  };
}

const calculateRequestForUngroupWidget = (groupWidget, widgets, breakpoint) => {
  const layout = _.get(groupWidget, `properties.${breakpoint}.layout`);
  const groupWidgetRect = getBoundingRectFromLayout(layout);

  const updatedWidgets = widgets.map(item => {
    const layout = _.get(item, `properties.${breakpoint}.layout`);
    const rect = getBoundingRectFromLayout(layout);

    const frame = new Frame(layout);

    if (frame.has('transform', 'translate')) {
      const newX = groupWidgetRect.x + rect.x;
      const newY = groupWidgetRect.y + rect.y;

      frame.set('transform', 'translate', `${newX}px, ${newY}px`);
    }

    return {
      ...item,
      properties: {
        ...item.properties,
        [breakpoint]: {
          ...item.properties[breakpoint],
          layout: frame.get()
        }
      }
    }
  });

  return updatedWidgets;
}

export default function* groupSaga() {
  yield takeEvery(EDITOR_CREATE_GROUP_WIDGET, createGroupWidgetSaga);
  yield takeEvery(EDITOR_UNGROUP_WIDGET, unGroupWidgetSaga);
  yield takeEvery(EDITOR_DELETE_WIDGET, deleteWidgetInGroupSaga);
};